import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import PropTypes from "prop-types";

const ConfirmDialog = ({
  open,
  title,
  description,
  onClose,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "info",
  loading = false,
}) => {
  const getIcon = () => {
    switch (type) {
      case "delete":
        return <WarningAmberIcon sx={{ color: "#DC2626", fontSize: 20 }} />;
      case "restore":
        return <RestoreIcon sx={{ color: "#16A34A", fontSize: 20 }} />;
      default:
        return <InfoOutlinedIcon sx={{ color: "#0284C7", fontSize: 20 }} />;
    }
  };

  const getBgColor = () => {
    switch (type) {
      case "delete":
        return "#FEF2F2";
      case "restore":
        return "#ECFDF5";
      default:
        return "#EFF6FF";
    }
  };

  const getConfirmColor = () => {
    switch (type) {
      case "delete":
        return "error";
      case "restore":
        return "success";
      default:
        return "primary";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          px: 1,
          py: 1,
        },
      }}
    >
      {/* Header */}
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: getBgColor(),
            }}
          >
            {getIcon()}
          </Box>

          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent>
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: "#475467",
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "10px",
            px: 3,
            textTransform: "none",
            color: "#475467",
            borderColor: "#D0D5DD",
            backgroundColor: "#F9FAFB",
            "&:hover": {
              backgroundColor: "#F2F4F7",
              borderColor: "#D0D5DD",
            },
          }}
        >
          {cancelText}
        </Button>

        <Button
          onClick={onConfirm}
          variant="contained"
          color={getConfirmColor()}
          disabled={loading}
          sx={{
            borderRadius: "10px",
            px: 3,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  type: PropTypes.oneOf(["delete", "restore", "info"]),
  loading: PropTypes.bool,
};

export default ConfirmDialog;